import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { LinksFunction, LoaderFunctionArgs, SerializeFrom } from '@remix-run/node';
import {
	Link,
	Links,
	Meta,
	MetaFunction,
	Outlet,
	Scripts,
	ScrollRestoration,
	isRouteErrorResponse,
	useLoaderData,
	useMatches,
	useRouteError,
} from '@remix-run/react';
import { GetCategoriesDocument, GetGlobalContentDocument, GetWarehousesDocument } from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonDay, serverEnv, withAlgoliaSearch } from '@theprintbar/server';
import { KansasFontLinks, WebRoutes, cn, invariantNotFound } from '@theprintbar/shared';
import {
	ActionToast,
	BrandIcon,
	BrandLogo,
	Container,
	GARootScript,
	GTMRootScript,
	GlobalPreviewProvider,
	MetaPixelScript,
	OutlineLinkButton,
	PageLoadingSpinner,
	UAScript,
} from '@theprintbar/ui';
import { useEffect } from 'react';
import { ExternalScripts } from 'remix-utils/external-scripts';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import tailwindStylesheetUrl from './tailwind.css?url';
import { getOrganisationStructuredData } from './utils/getOrganisationStructuredData';
import { getSiteStructuredData } from './utils/getSiteStructuredData';

export function getCanonicalUrl(href: string): string {
	const url = new URL(href);
	const res = url.origin + url.pathname;

	const page = url.searchParams.get('page');

	return res + (page ? `?page=${encodeURIComponent(page)}` : '');
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
	return [
		{
			title: 'Print Bar | Say something on something',
		},
		{
			name: 'og:url',
			content: location.pathname,
		},
		getOrganisationStructuredData(data),
		getSiteStructuredData(data),
	];
};

export const links: LinksFunction = () => [
	{ rel: 'icon', type: 'image/gif', href: '/favicon.gif' },
	{ rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
	{ rel: 'preload', as: 'style', href: tailwindStylesheetUrl },
	{ rel: 'stylesheet', href: tailwindStylesheetUrl },
	...KansasFontLinks,
];

export type RootLoaderData = SerializeFrom<typeof loader>;
export const loader = async ({ request }: LoaderFunctionArgs) => {
	const [globalContentRes, { warehouses }, { categories }] = await Promise.all([
		runSudoGraphql(GetGlobalContentDocument),
		runSudoGraphql(GetWarehousesDocument),
		runSudoGraphql(GetCategoriesDocument),
	]);

	invariantNotFound(globalContentRes.globalContent, 'Couldnt find global content');
	return cachedJsonDay({
		environment: serverEnv.ENVIRONMENT,
		SENTRY_DSN: process.env.SENTRY_DSN,
		isDev: ['local', 'dev'].includes(serverEnv.ENVIRONMENT),
		globalContent: globalContentRes.globalContent,
		GATrackingId: serverEnv.GA_TRACKING_ID,
		metaPixelId: serverEnv.META_PIXEL_ID,
		universalAnalyticsId: serverEnv.UNIVERSAL_ANALYTICS_ID,
		GTMId: serverEnv.GTM_TRACKING_ID,
		webDomain: serverEnv.WEB_DOMAIN,
		canonicalUrl: getCanonicalUrl(request.url),
		assetsUrl: serverEnv.ASSET_DOMAIN,
		cesdkAssetsUrl: serverEnv.S3_ASSETS_URL,
		imglyLicence: serverEnv.IMGLY_KEY,
		adminDomain: serverEnv.ADMIN_DOMAIN,
		partnerDomain: serverEnv.PARTNER_DOMAIN,
		crewDomain: serverEnv.CREW_DOMAIN,
		klaviyoId: serverEnv.KLAVIYO_ID,
		recaptchaSiteKey: serverEnv.RECAPTCHA_SITE_KEY,
		GCPApiKey: serverEnv.GCP_API_KEY,
		categories,
		warehouses,
		...withAlgoliaSearch(),
	});
};

export function Layout({ children }: { children: React.ReactNode }) {
	const loaderData = useLoaderData<typeof loader>();

	return (
		<html lang="en">
			<head>
				{loaderData && (
					<>
						<GARootScript gaTrackingId={loaderData.GATrackingId} />
						<MetaPixelScript metaPixelId={loaderData.metaPixelId} />
						<GTMRootScript GTMId={loaderData.GTMId} />
						<UAScript universalAnalyticsId={loaderData.universalAnalyticsId} />
					</>
				)}
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				{!loaderData?.webDomain.includes('theprintbar.com') && <meta name="robots" content="noindex, nofollow" />}
				{/* render root canonical only if no child route provided one */}
				{loaderData?.canonicalUrl && <link rel="canonical" href={loaderData.canonicalUrl} />}
				<Meta />
				<Links />
			</head>
			<body className="relative h-full w-full bg-white-pearl text-blue antialiased">
				<PageLoadingSpinner />
				<GlobalPreviewProvider webDomain={loaderData.webDomain}>{children}</GlobalPreviewProvider>
				<ScrollRestoration />
				<ExternalScripts />
				<Scripts />
			</body>
		</html>
	);
}

export function useRootData() {
	const routes = useMatches();

	const rootData = routes.find(route => !!(route.data as SerializeFrom<typeof loader>)?.globalContent)?.data;

	return rootData as SerializeFrom<typeof loader>;
}

export const ErrorBoundary = () => {
	const error = useRouteError();

	useEffect(() => {
		void import('@sentry/remix')
			.then(Sentry => {
				Sentry.captureRemixErrorBoundaryError(error);
			})
			.catch(() => {});
	}, [error]);

	return (
		<>
			<header className="bg-white-pearl sticky top-0 z-50">
				{/* Main Menu Bar */}
				<nav className={cn('flex border-t border-blue')}>
					<Container className="relative flex items-center justify-between">
						{/* Logo */}
						<div className="flex flex-1">
							<Link to={WebRoutes.Home} className="flex items-center h-14 lg:h-16">
								<span className="sr-only">The Print Bar Logo</span>
								<BrandLogo className="fill-blue hidden xs:block h-5 w-auto lg:h-8" />
								<BrandIcon className="fill-blue h-8 w-auto xs:hidden" />
							</Link>
						</div>
					</Container>
				</nav>
			</header>
			<div className="flex justify-center mt-8 mb-16">
				{isRouteErrorResponse(error) ? (
					<div className="flex justify-center flex-col items-center">
						<img
							src={error.status === 404 ? '/images/waiting.gif' : '/images/error.png'}
							className="w-72 h-72 md:w-96 md:h-96"
						/>
						<h5 className="text-5xl md:text-7xl uppercase font-bold mt-4 md:mt-8">{error.status}</h5>
						<p className="text-lg md:text-2xl mb-4">{error.status === 404 ? "We couldn't find that page" : error.data}</p>
						<OutlineLinkButton icon={ArrowRightIcon} to="/">
							Return home
						</OutlineLinkButton>
					</div>
				) : (
					<div className="flex justify-center flex-col items-center">
						<img src="/images/error.png" className="w-72 h-72 md:w-96 md:h-96" />
						<h5 className="text-2xl md:text-4xl uppercase font-bold mt-4 md:mt-8">Something went wrong</h5>
						<p className="text-lg mb-4">Please reach out if this problem persists</p>
						<OutlineLinkButton icon={ArrowRightIcon} to="/">
							Return home
						</OutlineLinkButton>
					</div>
				)}
			</div>
		</>
	);
};

export default function App() {
	const loaderData = useLoaderData<typeof loader>();

	useEffect(() => {
		if (!loaderData.SENTRY_DSN) {
			console.log('Sentry DSN not found, skipping init');
			return;
		}

		import('@sentry/react')
			.then(Sentry => {
				if (Sentry.getCurrentScope().getClient() !== undefined) {
					console.log('Sentry client already initialized, skipping init');
					return;
				}
				Sentry.init({
					dsn: loaderData.SENTRY_DSN,
					profilesSampleRate: 0,
					tracesSampleRate: 0,
					replaysSessionSampleRate: 0,
					replaysOnErrorSampleRate: 0,
				});
			})
			.catch(() => {});
	}, []);

	return (
		<>
			<ActionToast />
			<Header data={loaderData} />
			<main className="relative flex flex-col w-full">
				<Outlet />
			</main>
			<Footer data={loaderData} />
		</>
	);
}
